import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import closeIcon from "assets/icons/x-circle.svg";
import Button from "components/buttons/Button";
import {
  setTimeFormat,
  TimeFormat,
  useTimeFormat,
} from "helpers/timeFormat";

import styles from "./SettingsModal.module.scss";

type Props = {
  onClose: () => void;
};

const SettingsModal: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const currentFormat = useTimeFormat();
  const [selectedFormat, setSelectedFormat] = useState<TimeFormat>(currentFormat);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleSave = () => {
    setTimeFormat(selectedFormat);
    onClose();
  };

  return (
    <div className={styles.wrapper} role="presentation" onClick={onClose}>
      <section
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="time-format-settings-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 id="time-format-settings-title">{t("settings")}</h2>
          <button type="button" onClick={onClose} aria-label={t("closeSettings")}>
            <img src={closeIcon} alt="" />
          </button>
        </header>

        <div className={styles.content}>
          <div>
            <h3>{t("timeFormat")}</h3>
            <p>{t("timeFormatDescription")}</p>
          </div>

          <div className={styles.options}>
            {(["24h", "12h"] as TimeFormat[]).map((format) => (
              <label
                key={format}
                className={selectedFormat === format ? styles.active : ""}
              >
                <input
                  type="radio"
                  name="time-format"
                  value={format}
                  checked={selectedFormat === format}
                  onChange={() => setSelectedFormat(format)}
                />
                <span>
                  <strong>
                    {format === "24h" ? t("twentyFourHour") : t("twelveHour")}
                  </strong>
                  <small>{format === "24h" ? "23:25" : "11:25 PM"}</small>
                </span>
              </label>
            ))}
          </div>
        </div>

        <footer className={styles.footer}>
          <Button isSecondary txt={t("cancel")} handleClick={onClose} />
          <Button isPrimary txt={t("save")} handleClick={handleSave} />
        </footer>
      </section>
    </div>
  );
};

export default SettingsModal;
